import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CtmsTask, FULL_ACCESS_ROLES, resolveTasks, delegationStatus } from '@/lib/ctms/taskCatalog';

/** Current user's CTMS tasks at a site, optionally narrowed to one trial. */
export function useCtmsTasks(siteId: string | undefined, trialId: string | null) {
  const { data, isLoading } = useQuery({
    queryKey: ['ctms-access', siteId],
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 60 * 1000,
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !siteId) return null;
      const [adminResult, staffResult] = await Promise.all([
        supabase.rpc('user_has_admin_role', { check_user_id: user.id }),
        supabase
          .from('trial_site_staff')
          .select('id, staff_role, is_primary')
          .eq('trial_site_id', siteId)
          .eq('user_id', user.id)
          .is('end_date', null)
          .maybeSingle(),
      ]);
      if (adminResult.error) throw adminResult.error;
      if (staffResult.error) throw staffResult.error;
      const isAdmin = adminResult.data;
      const staff = staffResult.data;
      let delegations: { trial_id: string | null; delegated_tasks: string[] }[] = [];
      if (staff?.id) {
        const { data: d, error } = await supabase
          .from('delegation_of_authority')
          .select('trial_id, delegated_tasks, status, effective_date, expiration_date')
          .eq('trial_site_id', siteId)
          .eq('staff_id', staff.id);
        if (error) throw error;
        delegations = (d || []).filter((r) => delegationStatus(r) === 'active');
      }
      return {
        isAdmin: !!isAdmin,
        role: (staff?.staff_role as string) ?? null,
        isPrimary: !!staff?.is_primary,
        delegations,
      };
    },
  });

  const tasks = data
    ? resolveTasks({ ...data, trialId })
    : new Set<CtmsTask>();
  const canManage = !!data && (data.isAdmin || data.isPrimary || (!!data.role && FULL_ACCESS_ROLES.includes(data.role)));
  return { tasks, can: (t: CtmsTask) => tasks.has(t), canManage, isLoading };
}
